/**
 * SearchReplaceStrategy — surgical SEARCH/REPLACE block edits.
 *
 * The model returns one or more `{ search, replace }` blocks. Each `search`
 * must be a contiguous chunk that appears EXACTLY ONCE in the current file.
 * Blocks are matched against the ORIGINAL file content (order-invariant)
 * and then applied in reverse-order so earlier coordinates stay valid.
 *
 * This module currently implements the exact-match tier only. Subsequent
 * tasks add whitespace-normalised and Levenshtein fuzzy fallbacks.
 */
import { tolerantJsonParse, TolerantJsonError } from '../../tolerantJson';
import type { SchemaSpec } from '../../llm';
import type { EditStrategy, EditApplyResult } from './types';

const SEARCH_REPLACE_SCHEMA: SchemaSpec = {
  name: 'search_replace_output',
  strict: true,
  schema: {
    type: 'object',
    properties: {
      blocks: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            search: { type: 'string' },
            replace: { type: 'string' },
          },
          required: ['search', 'replace'],
          additionalProperties: false,
        },
      },
    },
    required: ['blocks'],
    additionalProperties: false,
  },
};

interface Block {
  search: string;
  replace: string;
}

/**
 * Build a normalised copy of `content` along with a parallel `origIndex`
 * array such that, for every character at position `i` in `normalised`,
 * `origIndex[i]` is the position in `content` that character came from.
 *
 * Normalisation rules (applied per line):
 *   - Strip leading whitespace from each line.
 *   - Collapse runs of internal whitespace to a single space.
 * Newlines are preserved between lines and mapped to the original newline.
 */
function buildNormalisedContent(content: string): { normalised: string; origIndex: number[] } {
  let normalised = '';
  const origIndex: number[] = [];
  const lines = content.split('\n');
  let pos = 0;
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    let i = 0;
    while (i < line.length && /\s/.test(line[i])) i++;
    let prevWasSpace = false;
    for (; i < line.length; i++) {
      const ch = line[i];
      if (/\s/.test(ch)) {
        if (prevWasSpace) continue;
        prevWasSpace = true;
        normalised += ' ';
        origIndex.push(pos + i);
      } else {
        prevWasSpace = false;
        normalised += ch;
        origIndex.push(pos + i);
      }
    }
    if (li < lines.length - 1) {
      normalised += '\n';
      origIndex.push(pos + line.length); // position of the newline char
    }
    pos += line.length + 1; // +1 for the newline
  }
  return { normalised, origIndex };
}

/** Same normalisation rules as `buildNormalisedContent` (no index map needed). */
function normaliseSearch(search: string): string {
  const out: string[] = [];
  const lines = search.split('\n');
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    let i = 0;
    while (i < line.length && /\s/.test(line[i])) i++;
    let prevWasSpace = false;
    let acc = '';
    for (; i < line.length; i++) {
      const ch = line[i];
      if (/\s/.test(ch)) {
        if (prevWasSpace) continue;
        prevWasSpace = true;
        acc += ' ';
      } else {
        prevWasSpace = false;
        acc += ch;
      }
    }
    out.push(acc);
  }
  return out.join('\n');
}

export class SearchReplaceStrategy implements EditStrategy {
  readonly name = 'search-replace' as const;
  readonly schema = SEARCH_REPLACE_SCHEMA;

  buildPromptAddition(): string {
    return [
      'OUTPUT FORMAT: Return JSON `{"blocks":[{"search":"...","replace":"..."}]}` — each block is a SEARCH/REPLACE pair.',
      'Each block\'s SEARCH MUST be a contiguous chunk that appears EXACTLY ONCE in the current file.',
      'Include 3-5 lines of unique surrounding context with the changed line(s) so the SEARCH is unambiguous.',
      'NO LINE NUMBERS — never prefix lines with numbers. Match against the actual file content.',
      'The matcher is tolerant of indentation/whitespace differences (leading whitespace and internal whitespace runs), but the SEARCH must still be UNAMBIGUOUS after normalisation.',
      'Multiple blocks are allowed and applied order-invariantly against the original file.',
      'Right-size the edit to the FIX, not to brevity. If the fix is "tweak one line", emit a 1-line block. If the fix is "add an entire missing method / addApi handler / event listener", your REPLACE field MUST contain that whole new block — search for an anchor (constructor body, init() method opening, end of class) and include the new code in REPLACE. Edits that minimise wire-on-page while leaving the missing functionality unfilled are MORE harmful than verbose-but-correct edits.',
    ].join('\n');
  }

  apply(modelOutput: string, currentContent: string, _path: string): EditApplyResult {
    // tolerantJsonParse returns { value, strategy } — read .value off the result.
    let value: unknown;
    try {
      value = tolerantJsonParse(modelOutput).value;
    } catch (err) {
      const detail = err instanceof TolerantJsonError ? err.cause : String(err);
      return { ok: false, reason: 'parse-failed', detail };
    }
    const blocks = (value as { blocks?: unknown })?.blocks;
    if (!Array.isArray(blocks) || blocks.length === 0) {
      return { ok: false, reason: 'empty', detail: 'no blocks provided' };
    }

    // Lazily-built whitespace-normalised view of the current content; only
    // computed if at least one block fails exact match.
    let normCache: { normalised: string; origIndex: number[] } | null = null;
    const getNorm = () => {
      if (!normCache) normCache = buildNormalisedContent(currentContent);
      return normCache;
    };

    // Match each block against the ORIGINAL content (order-invariant). For
    // each block we try exact-match first, then whitespace-normalised
    // fallback. Compute per-block replacement coords first; apply in
    // reverse-order so earlier coords stay valid.
    const replacements: Array<{ start: number; end: number; replace: string }> = [];
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i] as Block;
      if (typeof block.search !== 'string' || typeof block.replace !== 'string') {
        return { ok: false, reason: 'parse-failed', detail: `block ${i} missing search/replace strings` };
      }

      // Tier 1: exact match.
      const first = currentContent.indexOf(block.search);
      if (first >= 0) {
        const second = currentContent.indexOf(block.search, first + 1);
        if (second >= 0) {
          return { ok: false, reason: 'ambiguous', detail: `block ${i}: SEARCH matches more than once` };
        }
        replacements.push({ start: first, end: first + block.search.length, replace: block.replace });
        continue;
      }

      // Tier 2: whitespace-normalised match.
      const { normalised, origIndex } = getNorm();
      const normSearch = normaliseSearch(block.search);
      if (normSearch.length === 0) {
        return { ok: false, reason: 'no-match', detail: `block ${i}: SEARCH normalises to empty` };
      }
      const normFirst = normalised.indexOf(normSearch);
      if (normFirst < 0) {
        return { ok: false, reason: 'no-match', detail: `block ${i}: SEARCH not found (even normalised)` };
      }
      const normSecond = normalised.indexOf(normSearch, normFirst + 1);
      if (normSecond >= 0) {
        return { ok: false, reason: 'ambiguous', detail: `block ${i}: SEARCH matches more than once (normalised)` };
      }
      const origStart = origIndex[normFirst];
      const origEndExclusive = origIndex[normFirst + normSearch.length - 1] + 1;
      replacements.push({ start: origStart, end: origEndExclusive, replace: block.replace });
    }

    replacements.sort((a, b) => b.start - a.start);
    let next = currentContent;
    for (const r of replacements) {
      next = next.slice(0, r.start) + r.replace + next.slice(r.end);
    }
    return { ok: true, newCode: next, appliedBlocks: replacements.length };
  }
}
